
import { useState } from 'react';

interface FAQItem {
  id: number;
  question: string;
  answer: string;
}

export default function FAQContent(): JSX.Element {
  const [openId, setOpenId] = useState<number | null>(1);

  const faqData: FAQItem[] = [
    {
      id: 1,
      question: '인테리어 비용은 보통 평당 얼마인가요?',
      answer:
        "인테리어 비용은 단순히 평수로 결정되기보다, 고객님의 라이프스타일과 선택하시는 마감재, 구조 변경 여부에 따라 큰 차이가 납니다. 프로젝트랩은 예산 안에서 최적의 퀄리티를 낼 수 있는 '가성비'와 '심미성'의 균형을 제안해 드립니다. 상세 견적은 구체적인 상담과 현장 실측 후 투명하게 안내해 드리고 있습니다.",
    },
    {
      id: 2,
      question: '공사 기간은 얼마나 잡아야 할까요?',
      answer:
        '일반적으로 아파트 전체 리모델링 기준, 약 3주에서 5주 정도 소요됩니다. 여기에는 철거, 설비, 목공, 마감 등 공정별 전문 인력의 정밀한 작업 시간이 포함됩니다. 공사 내용에 따라 기간은 조절될 수 있으며, 계약 시 상세 공정표를 통해 단계별 진행 상황을 공유해 드립니다.',
    },
    {
      id: 3,
      question: '상담을 받기 전, 무엇을 준비하면 좋을까요?',
      answer:
        "가장 중요한 것은 **'우리 가족이 이 공간에서 어떻게 살고 싶은가'**에 대한 고민입니다.\n\n• 레퍼런스 이미지: 선호하는 스타일의 사진 (3~5장 정도)\n• 공사 범위와 예산: 우선순위를 둔 공간과 최대 가용 예산\n• 평면도: 아파트 관리사무소나 포털 사이트에서 구할 수 있는 도면\n\n위의 항목들을 미리 준비해 주시면 훨씬 더 깊이 있는 공간 제안이 가능합니다.",
    },
    {
      id: 4,
      question: '살면서 하는 부분 인테리어도 가능한가요?',
      answer:
        '부분 인테리어도 가능합니다. 다만, 거주 중 공사는 소음과 먼지 발생, 짐 이동 등의 제약이 있어 비어 있는 집 공사보다 공정이 까다롭고 기간이 늘어날 수 있습니다. 상담 시 거주 여부를 말씀해 주시면, 고객님의 불편을 최소화할 수 있는 효율적인 공사 범위를 함께 논의해 드립니다.',
    },
    {
      id: 5,
      question: '공사 후 사후 관리(A/S)는 어떻게 보장되나요?',
      answer:
        '인테리어는 공사가 끝난 시점부터가 진짜 시작이라고 생각합니다. 저희는 시공 완료 후 1년간의 무상 A/S를 기본으로 보장하며, 이후에도 공간에 문제가 생기면 언제든 빠르게 대응해 드립니다. 프로젝트랩은 한 번의 인연이 지속적인 신뢰로 이어질 수 있도록 책임 있는 시공을 약속합니다.',
    },
  ];

  const toggleFAQ = (id: number) => {
    setOpenId(openId === id ? null : id);
  };

  return (
    <div className="pt-32 pb-24 px-6 lg:px-12">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-16">
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            자주 묻는 질문
          </h1>
          <p className="text-lg text-gray-600 leading-relaxed">
            고객님들이 가장 궁금해하시는 질문들을 모았습니다.
            <br />
            더 궁금하신 사항은 언제든 문의해 주세요.
          </p>
        </div>

        <div className="space-y-4">
          {faqData.map((faq) => (
            <div
              key={faq.id}
              className="border border-gray-200 rounded-lg overflow-hidden transition-all hover:shadow-md"
            >
              <button
                onClick={() => toggleFAQ(faq.id)}
                className="w-full px-8 py-6 flex items-center justify-between text-left cursor-pointer hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start gap-4 flex-1">
                  <span className="text-lg font-semibold text-gray-400 mt-1">
                    {faq.id}.
                  </span>
                  <h4 className="text-lg font-semibold text-gray-900 leading-relaxed">
                    {faq.question}
                  </h4>
                </div>
                <div className="w-6 h-6 flex items-center justify-center ml-4">
                  <i
                    className={`ri-arrow-down-s-line text-2xl text-gray-600 transition-transform duration-300 ${
                      openId === faq.id ? 'rotate-180' : ''
                    }`}
                  ></i>
                </div>
              </button>

              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openId === faq.id ? 'max-h-[800px]' : 'max-h-0'
                }`}
              >
                <div className="px-8 pb-6 pl-20">
                  <p className="text-base text-gray-700 leading-relaxed whitespace-pre-line">
                    {faq.answer}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 text-center">
          <p className="text-gray-600 mb-6">
            더 궁금하신 사항이 있으신가요?
          </p>
          <a
            href="https://form.naver.com/response/XAc4kYKR-2Wg14KJ-ilzcQ"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-8 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-all whitespace-nowrap cursor-pointer"
          >
            상담 예약하기
          </a>
        </div>
      </div>
    </div>
  );
}
